/**
 * A property's address, whichever way it was stored.
 *
 * Most properties in production (122 of 126 on 2026-10-09) have no
 * `address` object: the address only lives in `name`, e.g.
 * "1315 SE 2nd St, Fort Lauderdale, FL 33301". Reading only `address`
 * showed "Address unavailable" / "Property" and left contracts without a
 * state (so no state-specific sections).
 */

export type AddressParts = {
  full: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  county?: string;
};

type AddressLike = {
  fullAddress?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  county?: string;
};

type ProductLike = {
  name?: string;
  address?: AddressLike | string | null;
  refinance?: { property?: AddressLike | null } | null;
};

const STATE_ZIP = /^([A-Za-z]{2}|[A-Za-z][A-Za-z .]+?)\s*(\d{5}(?:-\d{4})?)?$/;

/** "123 Main St, Austin, TX 78701" → street / city / state / zip. */
export function parseAddressLine(
  line: string | undefined | null
): AddressParts {
  const full = String(line ?? '').trim();
  if (!full) return { full: '' };
  const parts = full
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p && !/^(USA?|United States( of America)?)$/i.test(p));
  if (parts.length < 3) return { full, street: parts[0] };
  const tail = parts[parts.length - 1];
  const m = tail.match(STATE_ZIP);
  return {
    full,
    street: parts.slice(0, parts.length - 2).join(', '),
    city: parts[parts.length - 2],
    state: m?.[1]?.trim(),
    zip: m?.[2],
  };
}

export function productAddressParts(
  product: ProductLike | undefined | null
): AddressParts {
  if (!product) return { full: '' };
  const raw = product.address;
  const a: AddressLike = raw && typeof raw === 'object' ? raw : {};
  const refi = product.refinance?.property ?? {};
  const fromName = parseAddressLine(
    typeof raw === 'string' && raw.trim() ? raw : product.name
  );
  const street = a.street ?? refi.street ?? fromName.street;
  const city = a.city ?? refi.city ?? fromName.city;
  const state = a.state ?? refi.state ?? fromName.state;
  const zip = a.zip ?? refi.zip ?? fromName.zip;
  const full =
    (a.fullAddress ?? refi.fullAddress ?? '').trim() ||
    [
      a.street ?? refi.street,
      a.city ?? refi.city,
      [a.state ?? refi.state, a.zip ?? refi.zip].filter(Boolean).join(' '),
    ]
      .filter(Boolean)
      .join(', ') ||
    fromName.full;
  return { full, street, city, state, zip, county: a.county ?? refi.county };
}

/** Display line for a property, or '' when nothing is known. */
export function productAddressLine(
  product: ProductLike | undefined | null
): string {
  return productAddressParts(product).full;
}

type ContractLike = {
  product?: ProductLike | string | null;
  sellerPropertyInfo?: { propertyAddress?: string } | null;
  agentSellerInfo?: { propertyAddress?: string } | null;
};

/**
 * A contract's property address, the same way the Deal Room header reads
 * it (the property first). Screens that preferred the seller's typed
 * address or a bare `address.street` showed different addresses for the
 * same deal.
 */
export function contractAddressLine(
  contract: ContractLike | undefined | null,
  product?: ProductLike | null
): string {
  const linked =
    contract?.product && typeof contract.product === 'object'
      ? contract.product
      : undefined;
  return (
    productAddressLine(linked) ||
    productAddressLine(product) ||
    contract?.sellerPropertyInfo?.propertyAddress?.trim() ||
    contract?.agentSellerInfo?.propertyAddress?.trim() ||
    ''
  );
}
